import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    InputAdornment,
    MenuItem,
    Paper,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { useTranslate } from 'react-admin';
import { REGIONS, addDepartmentCode, formatDepartment, searchDepartments, toggleRegion } from './departments';
import { toggleDepartmentCode } from './ignGeocode';
import { PrestataireScope } from './types';

const useStyles = makeStyles({
    suggestions: {
        position: 'absolute',
        zIndex: 20,
        left: 0,
        right: 0,
        marginTop: 4,
        maxHeight: 240,
        overflowY: 'auto',
    },
    suggestion: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 14,
        '&:hover, &:focus': {
            background: '#eef4fb',
            outline: 'none',
        },
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        maxHeight: 170,
        overflowY: 'auto',
        paddingTop: 4,
    },
    // Not color='primary': the global theme forces a solid primary background on those chips.
    chip: {
        backgroundColor: '#eef4fb',
        border: '1px solid #b9d2ec',
        color: '#0b3d6b',
        fontWeight: 500,
        '& .MuiChip-deleteIcon': {
            color: '#5b7da3',
            '&:hover': {
                color: '#0b3d6b',
            },
        },
    },
});

interface DepartmentSelectionPanelProps {
    departmentCodes: number[];
    onChange: (codes: number[]) => void;
    /** The other checked types, to copy from. */
    otherScopes: PrestataireScope[];
    onApplyToAll: () => void;
    typeLabel: (type: string) => string;
    disabled?: boolean;
}

const DepartmentSelectionPanel: React.FC<DepartmentSelectionPanelProps> = ({
    departmentCodes,
    onChange,
    otherScopes,
    onApplyToAll,
    typeLabel,
    disabled,
}) => {
    const classes = useStyles();
    const translate = useTranslate();
    const [query, setQuery] = useState('');
    const results = useMemo(() => searchDepartments(query), [query]);

    const pickDepartment = (code: number) => {
        setQuery('');
        onChange(addDepartmentCode(departmentCodes, code));
    };

    const regionCount = (codes: number[]) => codes.filter((code) => departmentCodes.includes(code)).length;

    return (
        <Box display='flex' flexDirection='column' style={{ gap: 12 }}>
            <Box position='relative'>
                <TextField
                    fullWidth
                    variant='outlined'
                    size='small'
                    disabled={disabled}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && results[0]) {
                            event.preventDefault();
                            pickDepartment(results[0].code);
                        }
                    }}
                    placeholder={translate('app.prestataires.fields.departmentSearch')}
                    inputProps={{ 'aria-label': translate('app.prestataires.fields.departmentSearch') }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' color='action' />
                            </InputAdornment>
                        ),
                    }}
                />
                {results.length > 0 && (
                    <Paper className={classes.suggestions} elevation={3}>
                        {results.map((department) => (
                            <button
                                key={department.code}
                                type='button'
                                className={classes.suggestion}
                                onClick={() => pickDepartment(department.code)}
                            >
                                <span>{formatDepartment(department.code)}</span>
                                {departmentCodes.includes(department.code) && <span>✓</span>}
                            </button>
                        ))}
                    </Paper>
                )}
            </Box>

            <TextField
                select
                id='prestataire-region'
                size='small'
                variant='outlined'
                disabled={disabled}
                value=''
                label={translate('app.prestataires.zone.addRegion')}
                onChange={(event) => {
                    const region = REGIONS.find((item) => item.name === event.target.value);
                    if (region) {
                        onChange(toggleRegion(departmentCodes, region));
                    }
                }}
            >
                {REGIONS.map((region) => {
                    const count = regionCount(region.departmentCodes);
                    return (
                        <MenuItem key={region.name} value={region.name}>
                            <Box display='flex' justifyContent='space-between' width='100%'>
                                <span>{region.name}</span>
                                <Typography variant='caption' color={count ? 'primary' : 'textSecondary'}>
                                    {count === region.departmentCodes.length ? '✓ ' : ''}
                                    {count}/{region.departmentCodes.length}
                                </Typography>
                            </Box>
                        </MenuItem>
                    );
                })}
            </TextField>

            {otherScopes.length > 0 && (
                <Box display='flex' flexWrap='wrap' style={{ gap: 8 }}>
                    <TextField
                        select
                        id='prestataire-copy-departments-from'
                        size='small'
                        variant='outlined'
                        disabled={disabled}
                        value=''
                        label={translate('app.prestataires.fields.copyDepartmentsFrom')}
                        style={{ flex: 1, minWidth: 160 }}
                        onChange={(event) => {
                            const source = otherScopes.find((scope) => scope.type === event.target.value);
                            if (source) {
                                onChange([...(source.departmentCodes || [])]);
                            }
                        }}
                    >
                        {otherScopes.map((scope) => (
                            <MenuItem key={scope.type} value={scope.type}>
                                {`${typeLabel(scope.type)} (${(scope.departmentCodes || []).length})`}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        size='small'
                        variant='outlined'
                        color='primary'
                        disabled={disabled || departmentCodes.length === 0}
                        onClick={onApplyToAll}
                    >
                        {translate('app.prestataires.zone.applyToAll')}
                    </Button>
                </Box>
            )}

            <Divider />

            <Box>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Typography variant='subtitle2'>
                        {translate('app.prestataires.zone.selectedCount', {
                            smart_count: departmentCodes.length,
                            count: departmentCodes.length,
                        })}
                    </Typography>
                    <Button
                        size='small'
                        disabled={disabled || departmentCodes.length === 0}
                        onClick={() => onChange([])}
                    >
                        {translate('app.prestataires.fields.clearDepartments')}
                    </Button>
                </Box>
                {departmentCodes.length === 0 ? (
                    <Typography variant='body2' color='textSecondary'>
                        {translate('app.prestataires.zone.emptySelection')}
                    </Typography>
                ) : (
                    <Box className={classes.chips}>
                        {departmentCodes.map((code) => (
                            <Chip
                                key={code}
                                size='small'
                                className={classes.chip}
                                label={formatDepartment(code)}
                                onDelete={disabled ? undefined : () => onChange(toggleDepartmentCode(departmentCodes, code))}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DepartmentSelectionPanel;
